import React, { useState, useEffect } from 'react';
import { getProfileActivationDetails } from '../../utils/profileActivation';
import { useUserProfile } from '../Auth/UserProfileContext';
import { useAuth } from '../Auth/AuthContext';
import debug from '../../utils/debug';
import GIGiFYLogo from '../../assets/Logo/GIGiFYbasicLogo.png';

const ProfileActiveBadge = ({
  position = 'fixed',
  top = '20px',
  right = '20px',
  transform = 'scale(1)',
  zIndex = 1000
} = {}) => {
  const { currentUser, userType } = useAuth();
  const { profile } = useUserProfile();
  const [isActivated, setIsActivated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [showStampAnimation, setShowStampAnimation] = useState(false);
  const [sparkles, setSparkles] = useState([]);
  const [showTriggerButton, setShowTriggerButton] = useState(false);

  // The badge-animation test button + its Shift+T shortcut are dev-only.
  const isDevMode = process.env.NODE_ENV === 'development';

  // Fetch activation status
  useEffect(() => {
    const fetchActivationStatus = async () => {
      if (!currentUser?.uid || !userType) {
        setIsLoading(false);
        return;
      }

      try {
        const details = await getProfileActivationDetails(currentUser.uid, userType);
        const wasActivated = details?.isActivated || false;
        setIsActivated(wasActivated);

        // Check if this is a fresh login (not a page reload)
        const loginSessionKey = `badge_login_session_${currentUser.uid}`;
        const currentSession = sessionStorage.getItem(loginSessionKey);

        // If no session marker exists, this is a fresh login!
        if (wasActivated && !currentSession) {
          debug.log('ProfileActiveBadge', '🎉 FRESH LOGIN DETECTED - TRIGGERING STAMP ANIMATION!');

          // Mark this session so animation doesn't replay on reload
          sessionStorage.setItem(loginSessionKey, Date.now().toString());

          // Trigger the stamp animation!
          triggerStampAnimation();
        }

        debug.log('ProfileActiveBadge', 'Profile activated:', wasActivated);
      } catch (error) {
        debug.error('ProfileActiveBadge', 'Error fetching activation status:', error);
        setIsActivated(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivationStatus();
  }, [currentUser?.uid, userType]);

  // Keyboard shortcut to toggle trigger button (Shift + T) — dev mode only.
  useEffect(() => {
    if (!isDevMode) return;

    const handleKeyPress = (e) => {
      if (e.shiftKey && e.key === 'T') {
        setShowTriggerButton(prev => {
          const newValue = !prev;
          debug.log('ProfileActiveBadge', `Trigger button ${newValue ? 'SHOWN' : 'HIDDEN'}`);
          return newValue;
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isDevMode]);

  // Create sparkle particles with enhanced variety
  const createSparkles = () => {
    const sparkleCount = 30; // More sparkles!
    const newSparkles = [];

    for (let i = 0; i < sparkleCount; i++) {
      // Sparkles shoot out from left, right, and top
      const pattern = i % 3;
      let side, angle, velocity;

      if (pattern === 0) {
        // Left side
        side = 'left';
        angle = -140 + (Math.random() * 80); // Wider spread
        velocity = 120 + Math.random() * 180;
      } else if (pattern === 1) {
        // Right side
        side = 'right';
        angle = -40 + (Math.random() * 80); // Wider spread
        velocity = 120 + Math.random() * 180;
      } else {
        // Top side (new!)
        side = 'top';
        angle = -110 + (Math.random() * 40); // Upward burst
        velocity = 100 + Math.random() * 150;
      }

      const delay = Math.random() * 250; // More stagger

      newSparkles.push({
        id: i,
        side,
        angle,
        velocity,
        delay,
        size: 2 + Math.random() * 6, // Varying sizes
        color: ['#FFD700', '#FFA500', '#FF6347', '#FFFFFF', '#01A2F1', '#F85008'][Math.floor(Math.random() * 6)], // Added brand colors
        rotation: Math.random() * 360 // Random rotation for more variety
      });
    }

    setSparkles(newSparkles);
  };

  // Trigger animation manually (for testing)
  const triggerStampAnimation = () => {
    debug.log('ProfileActiveBadge', '🎬 MANUALLY TRIGGERING STAMP ANIMATION');
    setShowStampAnimation(true);

    // Create sparkles after the stamp impact (1.2s delay)
    setTimeout(() => {
      createSparkles();
    }, 1200);

    // Clean up sparkles after animation completes
    setTimeout(() => {
      setShowStampAnimation(false);
      setSparkles([]);
    }, 3500);
  };

  // Don't render if loading or not activated
  if (isLoading || !isActivated) {
    return null;
  }

  // Pulsing dot indicator - smaller version
  const PulsingDot = () => (
    <div
      style={{
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: '#00ff00',
        boxShadow: '0 0 8px rgba(0, 255, 0, 0.6)',
        animation: 'pulse 2s infinite',
        marginRight: '2px'
      }}
    />
  );

  return (
    <>
      {/* Developer Test Button - Toggle with Shift+T (dev mode only) */}
      {isDevMode && showTriggerButton && (
        <button
          onClick={triggerStampAnimation}
          style={{
            position: 'fixed',
            top: '-15px',
            right: '70rem',
            padding: '12px 20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: '2px solid white',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '14px',
            cursor: 'pointer',
            zIndex: 9999,
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s ease',
            fontFamily: 'Montserrat, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.3)';
          }}
          title="Click to replay the badge animation | Press Shift+T to hide this button"
        >
          🎬 Trigger Badge Animation
        </button>
      )}

      <style>
        {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 8px rgba(0, 255, 0, 0.6);
              transform: scale(1);
            }
            50% {
              box-shadow: 0 0 15px rgba(0, 255, 0, 0.9);
              transform: scale(1.2);
            }
            100% {
              box-shadow: 0 0 8px rgba(0, 255, 0, 0.6);
              transform: scale(1);
            }
          }

          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }

          @keyframes glow {
            0%, 100% {
              filter: drop-shadow(0 0 5px rgba(248, 80, 8, 0.5));
            }
            50% {
              filter: drop-shadow(0 0 10px rgba(248, 80, 8, 0.8));
            }
          }

          /* 3D METAL STAMP ANIMATION */
          @keyframes metalStamp {
            0% {
              transform: perspective(1000px) translateZ(800px) translateY(-300px) rotateX(45deg) scale(0.3);
              opacity: 0;
              filter: brightness(2) drop-shadow(0 0 40px rgba(255, 255, 255, 0.8));
            }
            40% {
              transform: perspective(1000px) translateZ(200px) translateY(-50px) rotateX(15deg) scale(0.7);
              opacity: 1;
              filter: brightness(1.5) drop-shadow(0 0 20px rgba(255, 255, 255, 0.6));
            }
            55% {
              /* Impact moment - aggressive slam */
              transform: perspective(1000px) translateZ(0) translateY(0) rotateX(0deg) scale(1);
              filter: brightness(2.5) drop-shadow(0 0 30px rgba(248, 80, 8, 1)) drop-shadow(0 0 50px rgba(255, 215, 0, 0.8));
            }
            60% {
              /* Bounce back slightly */
              transform: perspective(1000px) translateZ(15px) translateY(-8px) rotateX(-3deg) scale(1.05);
              filter: brightness(2) drop-shadow(0 0 25px rgba(248, 80, 8, 0.8));
            }
            70% {
              /* Settle down with shake */
              transform: perspective(1000px) translateZ(5px) translateY(-2px) rotateX(1deg) scale(0.98) rotate(-2deg);
              filter: brightness(1.5) drop-shadow(0 0 15px rgba(248, 80, 8, 0.6));
            }
            80% {
              transform: perspective(1000px) translateZ(0) translateY(0) rotateX(0deg) scale(1.01) rotate(1deg);
              filter: brightness(1.2) drop-shadow(0 0 10px rgba(248, 80, 8, 0.4));
            }
            90% {
              transform: perspective(1000px) translateZ(0) translateY(0) rotateX(0deg) scale(1) rotate(-0.5deg);
              filter: brightness(1.1) drop-shadow(0 0 8px rgba(248, 80, 8, 0.3));
            }
            100% {
              transform: perspective(1000px) translateZ(0) translateY(0) rotateX(0deg) scale(1) rotate(0deg);
              filter: brightness(1) drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
            }
          }

          /* Impact flash effect */
          @keyframes impactFlash {
            0%, 54% {
              opacity: 0;
              transform: scale(0.8);
            }
            55% {
              opacity: 1;
              transform: scale(1.5);
            }
            60% {
              opacity: 0.7;
              transform: scale(2);
            }
            70% {
              opacity: 0;
              transform: scale(2.5);
            }
          }

          /* Metal ring effect */
          @keyframes metalRing {
            0%, 54% {
              opacity: 0;
              transform: scale(0.9);
              box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
            }
            55% {
              opacity: 1;
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.8);
            }
            65% {
              opacity: 0.5;
              transform: scale(1.4);
              box-shadow: 0 0 0 15px rgba(255, 255, 255, 0.4);
            }
            80% {
              opacity: 0;
              transform: scale(1.8);
              box-shadow: 0 0 0 30px rgba(255, 255, 255, 0);
            }
          }

          /* Sparkle particle animation - Enhanced with rotation and trail */
          @keyframes sparkleShoot {
            0% {
              transform: translate(0, 0) scale(1) rotate(0deg);
              opacity: 1;
              filter: blur(0px);
            }
            30% {
              opacity: 1;
              filter: blur(0px);
            }
            70% {
              opacity: 0.8;
              filter: blur(1px);
            }
            100% {
              transform: translate(var(--tx), var(--ty)) scale(0) rotate(var(--rotation));
              opacity: 0;
              filter: blur(2px);
            }
          }

          /* Sparkle twinkle - Enhanced */
          @keyframes sparkleTwinkle {
            0%, 100% {
              filter: brightness(1) saturate(1);
            }
            25% {
              filter: brightness(1.5) saturate(1.3);
            }
            50% {
              filter: brightness(2.5) saturate(1.8);
            }
            75% {
              filter: brightness(1.8) saturate(1.5);
            }
          }

          /* Screen shake effect */
          @keyframes screenShake {
            0%, 100% {
              transform: translate(0, 0);
            }
            10%, 30%, 50%, 70%, 90% {
              transform: translate(-2px, 1px);
            }
            20%, 40%, 60%, 80% {
              transform: translate(2px, -1px);
            }
          }

          /* Glow pulse on impact */
          @keyframes glowPulse {
            0%, 54% {
              opacity: 0;
            }
            55% {
              opacity: 1;
              box-shadow: 0 0 0 0 rgba(248, 80, 8, 0.9);
            }
            70% {
              opacity: 0.7;
              box-shadow: 0 0 0 40px rgba(248, 80, 8, 0);
            }
            100% {
              opacity: 0;
              box-shadow: 0 0 0 60px rgba(248, 80, 8, 0);
            }
          }
        `}
      </style>

      <div
        style={{
          position: position,
          top: top,
          right: right,
          background: 'linear-gradient(107deg, rgba(20, 72, 116, 1), rgba(248, 80, 8, 1))',
          padding: '8px 16px 8px 10px',
          borderRadius: '50px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: isHovered
            ? '0 6px 20px rgba(1, 162, 241, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            : '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
          border: '2px solid white',
          zIndex: zIndex,
          transition: showStampAnimation ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer',
          transform: transform,
          overflow: 'visible',
          minWidth: 'fit-content',
          backdropFilter: 'blur(8px)',
          backgroundColor: 'transparent',
          animation: showStampAnimation ? 'metalStamp 2s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : 'none',
          transformStyle: 'preserve-3d'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={triggerStampAnimation}
        title="Click to see your LIVE badge animation! Your profile is ready for gig opportunities!"
      >
        {/* Impact flash effect */}
        {showStampAnimation && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '100%',
              height: '100%',
              transform: 'translate(-50%, -50%)',
              background: 'radial-gradient(circle, rgba(255, 215, 0, 0.8) 0%, rgba(248, 80, 8, 0.4) 40%, transparent 70%)',
              borderRadius: '50%',
              animation: 'impactFlash 0.8s ease-out',
              pointerEvents: 'none',
              zIndex: -1
            }}
          />
        )}

        {/* Metal ring effect */}
        {showStampAnimation && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '100%',
              height: '100%',
              transform: 'translate(-50%, -50%)',
              border: '3px solid white',
              borderRadius: '50px',
              animation: 'metalRing 1s ease-out',
              pointerEvents: 'none',
              zIndex: -1
            }}
          />
        )}

        {/* Glow pulse effect - additional impact layer */}
        {showStampAnimation && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '120%',
              height: '120%',
              transform: 'translate(-50%, -50%)',
              borderRadius: '50px',
              animation: 'glowPulse 1.2s ease-out',
              pointerEvents: 'none',
              zIndex: -2
            }}
          />
        )}

        {/* Sparkle particles */}
        {sparkles.map(sparkle => {
          const radians = (sparkle.angle * Math.PI) / 180;
          const tx = Math.cos(radians) * sparkle.velocity;
          const ty = Math.sin(radians) * sparkle.velocity;

          // Position based on side
          let positionStyle = {};
          if (sparkle.side === 'left') {
            positionStyle = { top: '50%', left: '0%' };
          } else if (sparkle.side === 'right') {
            positionStyle = { top: '50%', left: '100%' };
          } else {
            // Top
            positionStyle = { top: '0%', left: '50%' };
          }

          return (
            <div
              key={sparkle.id}
              style={{
                position: 'absolute',
                ...positionStyle,
                width: `${sparkle.size}px`,
                height: `${sparkle.size}px`,
                background: sparkle.color,
                borderRadius: '50%',
                boxShadow: `0 0 ${sparkle.size * 3}px ${sparkle.color}, 0 0 ${sparkle.size * 6}px ${sparkle.color}`,
                animation: `sparkleShoot 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${sparkle.delay}ms forwards, sparkleTwinkle 0.4s ease-in-out ${sparkle.delay}ms infinite`,
                pointerEvents: 'none',
                '--tx': `${tx}px`,
                '--ty': `${ty}px`,
                '--rotation': `${sparkle.rotation}deg`,
                zIndex: 10
              }}
            />
          );
        })}

        {/* Shimmer effect overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.3) 50%, transparent 60%)',
            backgroundSize: '200% 100%',
            animation: isHovered ? 'shimmer 1.5s infinite' : 'none',
            pointerEvents: 'none',
            borderRadius: '50px'
          }}
        />

        {/* GIGiFY Logo */}
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          transform: isHovered ? 'scale(1.15) rotate(5deg)' : 'scale(1) rotate(0deg)',
          transition: 'transform 0.3s ease',
          filter: isHovered
            ? 'drop-shadow(0 0 6px rgba(248, 80, 8, 0.6))'
            : 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4))'
        }}>
          <img
            src={GIGiFYLogo}
            alt="GIGiFY"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* LIVE Text with Dot */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          position: 'relative'
        }}>
          <span style={{
            color: 'white',
            fontSize: '15px',
            fontWeight: '900',
            fontFamily: 'Montserrat, sans-serif',
            letterSpacing: '1.5px',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
            textTransform: 'uppercase'
          }}>
            LIVE
          </span>
          <PulsingDot />
        </div>
      </div>
    </>
  );
};

export default ProfileActiveBadge;
